// AI Governance API envelope helpers — promoted from
// src/AIAdoption/v2/ui.js so v1 components can share the same
// unwrap / extractError logic instead of manually walking statusCode.
//
// The v2 ui.js re-exports these names for backward compatibility, so
// existing imports of `unwrap` / `extractError` from './ui' or './v2/ui'
// continue to resolve.
//
// Why both layers? AIApi (the axios interceptor) wraps every backend
// response as:
//   {
//     statusCode: <original-body>,   // backends return {success, data, error}
//     errors:     <pulled-up error>,
//     httpStatus: 200|400|...,
//     headers:    {...},
//     etag:       '...',
//     notModified: false,
//   }
// AND backends return either:
//   { success: 1, data: {...} }                // happy path
//   { success: 0, error: '...', details: {} }  // sad path
//   { status:  1, response_data: {...} }       // legacy variant
//   { detail: '...' }                          // DRF default

// ─── unwrap — peels both envelopes and returns the inner data object ────────
export function unwrap(resp) {
    if (resp == null) return null;

    // Layer 1: peel AIApi interceptor wrapper.
    let body = resp;
    if (typeof resp === 'object' && 'statusCode' in resp
            && ('httpStatus' in resp || 'errors' in resp || 'headers' in resp)) {
        body = resp.statusCode;
        // Some legacy endpoints add another `response_data` nesting; v2 doesn't.
        // The legacy v1 shape is {status, response_data: {message, data}} — after
        // peeling response_data, the inner `data` is the real payload. v2 inlines
        // its data on the top body, so we only peel response_data.data when it
        // exists AND wraps a non-trivial object.
        if (body && typeof body === 'object' && body.response_data
                && typeof body.response_data === 'object') {
            body = body.response_data;
            if (body && typeof body === 'object' && body.data
                    && typeof body.data === 'object') {
                return body.data;
            }
        }
    }
    if (body == null) return null;

    // Layer 2: peel NMT envelope.
    if (body.success === 1 || body.success === 0
            || body.status === 1 || body.status === 0) {
        return body.data ?? null;
    }
    return body;
}

// ─── extractError — pulls the human-readable error message from any envelope ─
export function extractError(resp) {
    if (resp == null) return null;

    // AIApi interceptor sets `errors` at the top level when the body contains
    // an error object. Pull that first — it may be a string, a {message,...}
    // object, OR a DRF field-map like {field_name: ["msg"]}.
    if (resp.errors) {
        const fromInterceptor = _stringifyErrorBlob(resp.errors);
        if (fromInterceptor) return fromInterceptor;
    }

    // Peel the interceptor wrapper if present
    let body = resp;
    if (typeof resp === 'object' && 'statusCode' in resp
            && ('httpStatus' in resp || 'errors' in resp || 'headers' in resp)) {
        body = resp.statusCode;
    }
    if (body == null) return null;

    // Successful envelope → no error
    if (body.success === 1 || body.status === 1) return null;

    if (body.error) {
        const fromBody = _stringifyErrorBlob(body.error);
        if (fromBody) return fromBody;
    }
    if (body.detail) return String(body.detail);
    if (Array.isArray(body.non_field_errors) && body.non_field_errors.length) {
        return String(body.non_field_errors[0]);
    }

    // DRF serializer-style {field: ["error"]} maps at the top level
    return _stringifyErrorBlob(body);
}

function _stringifyErrorBlob(v) {
    if (v == null) return null;
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) {
        for (const item of v) {
            const s = _stringifyErrorBlob(item);
            if (s) return s;
        }
        return null;
    }
    if (typeof v !== 'object') return null;
    if (typeof v.message === 'string') return v.message;
    for (const [k, val] of Object.entries(v)) {
        if (k === 'error_code' || k === 'success' || k === 'status' || k === 'data') continue;
        const s = _stringifyErrorBlob(val);
        if (s) return /^[a-z_]+$/i.test(k) ? `${k}: ${s}` : s;
    }
    return null;
}
